import { useRef, useEffect } from "react";

// ─────────────────────────────────────────────────────────────
// Standalone 4-digit PIN entry, themed. Referenced by
// PaymentConfirmSheet but not included in the original file batch —
// built here to match the visual language already established by
// the legacy BillConfirmPage's inline PIN boxes (large rounded
// squares, emerald-filled on entry, red on error), just themed and
// wired as a controlled component.
//
// Props:
//   value     string (0-4 digits)
//   onChange  (value: string) => void — fires on every keystroke;
//             parent is expected to auto-submit at length 4
//   label     string
//   error     string | undefined
//   disabled  boolean
// ─────────────────────────────────────────────────────────────
export default function PinInput({ value = "", onChange, label, error, disabled = false }) {
  const digits = value.split("").concat(Array(4).fill("")).slice(0, 4);
  const refs = useRef([]);

  useEffect(() => {
    // Autofocus first empty box on mount / reset
    const firstEmpty = digits.findIndex(d => !d);
    refs.current[firstEmpty === -1 ? 3 : firstEmpty]?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setDigitAt = (index, digit) => {
    const arr = value.split("");
    arr[index] = digit;
    onChange(arr.join("").slice(0, 4));
  };

  const handleChange = (index, raw) => {
    const d = raw.replace(/\D/g, "").slice(-1);
    if (!d) return;
    setDigitAt(index, d);
    if (index < 3) refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        setDigitAt(index, "");
      } else if (index > 0) {
        setDigitAt(index - 1, "");
        refs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted) {
      onChange(pasted);
      refs.current[Math.min(pasted.length, 3)]?.focus();
    }
  };

  return (
    <div className="flex flex-col items-center">
      {label && <p className="text-secondary text-sm font-semibold mb-5 text-center">{label}</p>}
      <div className="flex justify-center gap-3">
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={el => (refs.current[i] = el)}
            type="password"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            disabled={disabled}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            onPaste={handlePaste}
            className="w-14 h-14 text-center text-xl font-bold rounded-2xl border-2 outline-none transition-all disabled:opacity-50"
            style={{
              borderColor: error ? "var(--color-danger)" : digit ? "var(--color-brand)" : "var(--color-border-strong)",
              background:  error ? "var(--color-danger-light)" : digit ? "var(--color-brand-light)" : "var(--color-surface-muted)",
              color:       error ? "var(--color-danger-text)" : digit ? "var(--color-brand-text)" : "var(--color-text-primary)",
            }}
          />
        ))}
      </div>
      {error && <p className="text-danger text-xs mt-3 text-center" style={{ color: "var(--color-danger)" }}>{error}</p>}
    </div>
  );
}